import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { useMountEffect } from '@vista-digital/react-tools';
import { motion } from 'framer-motion';
import { observer } from 'mobx-react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getKeyCodeForEvent, KeyCode } from '../../../utils/key-code';
import { ExpandContentIndicator } from '../../expand-content-indicator/expand-content-indicator';
import { Heading } from '../../heading/heading';
import { AccordionContextInstance } from '../types/context';
const b = bemClassBuilder('v-accordion-section');
/**
 * Renders an accordion section.
 */
export const AccordionSection = observer(props => {
    const { className, id, title, subtitle, expandedByDefault, error, children } = props;
    const { headingLevel, expandIconName, setFocusedSectionId, addSectionId, removeSectionId, focusedSectionId } = useContext(AccordionContextInstance);
    const [isExpanded, setIsExpanded] = useState(expandedByDefault !== null && expandedByDefault !== void 0 ? expandedByDefault : false);
    const sectionHeaderRef = useRef(null);
    const isFocused = focusedSectionId === id;
    const sectionId = `section-${id}`;
    const labelId = `label-${id}`;
    useMountEffect(() => {
        addSectionId(id);
        return () => removeSectionId(id);
    });
    const handleFocus = useCallback(() => {
        setFocusedSectionId(id);
    }, [id, setFocusedSectionId]);
    const handleSectionToggled = useCallback(() => {
        setIsExpanded(!isExpanded);
        handleFocus();
    }, [handleFocus, isExpanded]);
    const handleKeyDown = (e) => {
        const keyCode = getKeyCodeForEvent(e);
        const isToggleKeyCode = keyCode === KeyCode.Space || keyCode === KeyCode.Enter;
        if (!isToggleKeyCode)
            return;
        handleSectionToggled();
        e.preventDefault();
    };
    useEffect(() => {
        if (isFocused && sectionHeaderRef.current) {
            sectionHeaderRef.current.focus();
        }
    });
    return (_jsxs("section", Object.assign({ className: b.block(undefined, className) }, { children: [_jsxs("header", Object.assign({ className: b.element('header'), role: "button", "aria-expanded": isExpanded, "aria-controls": sectionId, id: labelId, onKeyDown: handleKeyDown, tabIndex: 0, onFocus: handleFocus, onClick: handleSectionToggled, ref: sectionHeaderRef }, { children: [_jsxs("div", Object.assign({ className: b.element('header-details') }, { children: [_jsx(Heading, Object.assign({ level: headingLevel, className: b.element('title') }, { children: title })), subtitle && _jsx("div", Object.assign({ className: b.element('subtitle') }, { children: subtitle })), error] })), _jsx(ExpandContentIndicator, { isExpanded: isExpanded, iconName: expandIconName, className: b.element('expand-indicator') })] })), _jsx(motion.div, Object.assign({ id: sectionId, className: b.element('content'), role: "region", "aria-labelledby": labelId, "aria-hidden": !isExpanded, initial: isExpanded ? 'expanded' : 'collapsed', animate: isExpanded ? 'expanded' : 'collapsed', variants: {
                    expanded: { opacity: 1, height: 'auto' },
                    collapsed: { opacity: 0, height: 0 }
                }, transition: { type: 'tween', duration: 0.3, ease: 'easeInOut' } }, { children: children }))] })));
});
AccordionSection.displayName = 'AccordionSection';
